import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { resolveMedia } from "@/lib/api";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Silent, looping, in-view-only video thumbnail that links to the case study.
export const VideoTile = ({ project, className = "", aspect = "aspect-[4/5]", delay = 0 }) => {
  const ref = useRef(null);
  const vidRef = useRef(null);
  const [show, setShow] = useState(false);
  const [reduced] = useState(prefersReduced);
  const [manualPlay, setManualPlay] = useState(false);
  const posterUrl = resolveMedia(project.poster);
  const videoUrl = resolveMedia(project.video_src || project.videoSrc);

  // reveal on scroll
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced) {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShow(true);
          io.unobserve(e.target);
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  // play only while visible
  useEffect(() => {
    const v = vidRef.current;
    if (!v || reduced) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.4 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [reduced]);

  return (
    <Link
      to={`/creative/${project.slug}`}
      ref={ref}
      className={`group relative block overflow-hidden rounded-2xl bg-[var(--bg-contrast)] ${aspect} ${className}`}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "none" : "translateY(34px)",
        transition: "opacity 1s var(--ease), transform 1s var(--ease)",
        transitionDelay: `${delay}ms`,
      }}
      data-testid={`creative-tile-${project.slug}`}
    >
      <video
        ref={vidRef}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
        poster={posterUrl || undefined}
        src={reduced && !manualPlay ? undefined : videoUrl || undefined}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
      />

      {/* manual play for reduced motion */}
      {reduced && !manualPlay && (
        <button
          onClick={(e) => {
            e.preventDefault();
            setManualPlay(true);
            setTimeout(() => vidRef.current?.play().catch(() => {}), 50);
          }}
          className="absolute bottom-4 right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--mint)] text-[var(--charcoal)]"
          aria-label={`Play ${project.title}`}
        >
          <Play size={18} fill="currentColor" />
        </button>
      )}

      {/* gradient + metadata */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#141210]/85 via-[#141210]/10 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute inset-x-0 bottom-0 translate-y-1 p-6 transition-transform duration-500 group-hover:translate-y-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">
          {project.client} · {project.vertical}
        </p>
        <h3 className="mt-1.5 text-lg font-semibold tracking-tight text-white sm:text-xl">
          {project.title}
        </h3>
        <p className="mt-1 text-[13px] text-white/70">
          {((project.tags && project.tags[0]) || project.category) } · {project.date}
        </p>
      </div>
    </Link>
  );
};
